import * as Api from '@ViewModels';
import produce from 'immer';
import { useCallback, useMemo } from 'react';

/**
 * Hook for managing company tag filter state in the PeopleContainer.
 * Provides methods to get, set, and clear company tag filter criteria.
 *
 * @param filter - The current bulk contacts request filter
 * @param onChangeFilter - Callback to update the filter when changes are made
 * @returns Object containing companyTag value and methods to manipulate it
 */
export const useCompanyTagFilter = ({
	filter,
	onChangeFilter,
}: {
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}) => {
	const companyTag = useMemo(() => {
		const criterion = filter.filter?.criteria?.find(c => c.property === Api.ContactFilterCriteriaProperty.CompanyTag);
		return criterion?.value ?? '';
	}, [filter.filter?.criteria]);

	const setCompanyTag = useCallback(
		(value: string) => {
			const nextFilter = produce(filter, draftFilter => {
				if (!draftFilter.filter?.criteria) {
					return;
				}

				const existingIndex = draftFilter.filter.criteria.findIndex(
					c => c.property === Api.ContactFilterCriteriaProperty.CompanyTag
				);

				if (value.trim() === '') {
					// Remove criterion if value is empty
					if (existingIndex > -1) {
						draftFilter.filter.criteria.splice(existingIndex, 1);
					}
				} else {
					// Add or update criterion
					const newCriterion: Api.IContactFilterCriteria = {
						property: Api.ContactFilterCriteriaProperty.CompanyTag,
						value: value.trim(),
					};

					if (existingIndex > -1) {
						draftFilter.filter.criteria[existingIndex] = newCriterion;
					} else {
						draftFilter.filter.criteria.push(newCriterion);
					}
				}
			});

			onChangeFilter(nextFilter);
		},
		[filter, onChangeFilter]
	);

	const clearCompanyTagFilter = useCallback(() => {
		const nextFilter = produce(filter, draftFilter => {
			if (!draftFilter.filter?.criteria) {
				return;
			}
			draftFilter.filter.criteria = draftFilter.filter.criteria.filter(
				c => c.property !== Api.ContactFilterCriteriaProperty.CompanyTag
			);
		});

		onChangeFilter(nextFilter);
	}, [filter, onChangeFilter]);

	const hasCompanyTagFilter = useMemo(() => {
		return companyTag.trim() !== '';
	}, [companyTag]);

	return {
		companyTag,
		setCompanyTag,
		clearCompanyTagFilter,
		hasCompanyTagFilter,
	};
};
